import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getMeals,
  getMetrics,
  getNutritionDays,
  getNutritionSummary,
  getRecentFoods,
  getSettings,
  saveMeal,
  type NutritionDay,
  type NutritionSummary,
  type NutritionTargets,
  type RecentFood,
  type StoredMeal,
} from '../api.js';
import { itemTotals } from '../lib/draftItems.js';
import { localYmd, parseTargets, repeatMealPayload, targetProgress, weekDays } from '../lib/foodPage.js';
import { fmtNumber, sumPoints } from '../lib/format.js';
import { todayBounds } from '../lib/range.js';
import MealEntry from '../components/MealEntry.js';
import { Card, ErrorState, Group, Icon, MoreRow, Page, Ring, Row } from '../ui/index.js';

type EntryTab = 'scan' | 'describe';

/** Minutes to add to UTC to get local time (e.g. -240 for US Eastern DST). */
function localTzOffset(): number {
  return -new Date().getTimezoneOffset();
}

function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

const TARGET_UNITS: Record<string, string> = {
  calories: 'kcal',
  protein_g: 'g',
  fiber_g: 'g',
  sugar_g: 'g',
};

const RECENT_SHOWN = 5;

export default function Nutrition(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const [summary, setSummary] = useState<NutritionSummary | null>(null);
  const [meals, setMeals] = useState<StoredMeal[]>([]);
  const [recents, setRecents] = useState<RecentFood[]>([]);
  const [days, setDays] = useState<NutritionDay[]>([]);
  const [targets, setTargets] = useState<NutritionTargets>({});
  const [active, setActive] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [entryTab, setEntryTab] = useState<EntryTab>('describe');
  const [repeating, setRepeating] = useState<string | null>(null);
  const [logged, setLogged] = useState<string | null>(null);
  const [allRecents, setAllRecents] = useState(false);

  const reload = useCallback(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const { from, to } = todayBounds();
    const tz = localTzOffset();
    const now = new Date();
    const weekFrom = localYmd(new Date(now.getTime() - 6 * 86_400_000));
    const weekTo = localYmd(now);
    // Active calories only adjust "left"; a failure must not sink the page.
    getMetrics({ type: 'active_calories', from, to, bucket: 'raw', agg: 'sum' })
      .then((r) => {
        if (alive) setActive(r.points.length > 0 ? sumPoints(r.points) : null);
      })
      .catch(() => {
        if (alive) setActive(null);
      });
    Promise.all([
      getNutritionSummary(undefined, tz),
      getMeals(from, to),
      getRecentFoods(24),
      getNutritionDays(weekFrom, weekTo, tz),
      getSettings(),
    ])
      .then(([s, m, r, d, settings]) => {
        if (!alive) return;
        setSummary(s);
        setMeals(m);
        setRecents(r);
        setDays(d);
        setTargets(parseTargets(settings.nutrition_targets));
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : 'Failed to load food.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => reload(), [reload]);

  // Deep-link from Health's "Scan" button: open on the scan tab once, then
  // strip the param so a refresh doesn't reopen it.
  useEffect(() => {
    if (params.get('add') === 'scan') {
      setEntryTab('scan');
      setAdding(true);
      const next = new URLSearchParams(params);
      next.delete('add');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  function openEntry(tab: EntryTab): void {
    setEntryTab(tab);
    setAdding(true);
  }

  function onSaved(): void {
    setAdding(false);
    reload();
  }

  async function repeatFood(item: RecentFood): Promise<void> {
    setRepeating(item.key);
    try {
      await saveMeal(repeatMealPayload(item));
      setLogged(item.key);
      window.setTimeout(() => setLogged((k) => (k === item.key ? null : k)), 1600);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not log that food.');
    } finally {
      setRepeating(null);
    }
  }

  const progress = summary ? targetProgress(summary, targets) : [];
  const macros = progress.filter((p) => p.key !== 'calories');
  const week = weekDays(days);
  const todayYmd = localYmd(new Date());

  // Headline: remaining = goal − food + exercise (MyFitnessPal). Without a
  // calorie target there is nothing to subtract from, so show what was eaten.
  const eaten = summary?.calories ?? 0;
  const mealCount = days.find((d) => d.date === localYmd(new Date()))?.mealCount ?? meals.length;
  const goal = targets.calories;
  const burned = active ?? 0;
  const left = goal ? goal - eaten + burned : null;
  const budget = goal ? goal + burned : 0;

  const weekMax = Math.max(goal ?? 0, ...week.map((d) => d.calories ?? 0), 1);
  const shownRecents = allRecents ? recents : recents.slice(0, RECENT_SHOWN);

  return (
    <Page
      title="Food"
      sub={new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
    >
      {error && <ErrorState message={error} onRetry={reload} />}

      <Card className="fd-hero" label="Today's calories">
        <div className="hb-hero">
          <div className="hb-hero__text">
            <span className="hb-hero__label">{left !== null ? (left >= 0 ? 'Left today' : 'Over today') : 'Eaten today'}</span>
            <span className={`hb-hero__value${loading && !summary ? ' fd-dim' : ''}`}>
              {summary ? fmtNumber(Math.abs(left ?? eaten), 0) : '—'}
              <span className="hb-hero__unit">kcal</span>
            </span>
            <span className="hb-hero__sub">
              {goal ? (
                <>
                  <b className="num">{fmtNumber(goal, 0)}</b> goal − <b className="num">{fmtNumber(eaten, 0)}</b> food
                  {active !== null && (
                    <>
                      {' '}
                      + <b className="num">{fmtNumber(burned, 0)}</b> move
                    </>
                  )}
                </>
              ) : (
                <>
                  {mealCount} {mealCount === 1 ? 'meal' : 'meals'}
                  {summary && summary.protein_g > 0 ? ` · ${fmtNumber(summary.protein_g, 0)} g protein` : ''}
                </>
              )}
            </span>
          </div>
          {goal ? (
            <span className="hb-hero__aside">
              <Ring
                value={eaten}
                max={budget}
                color={left !== null && left < 0 ? 'var(--warn)' : 'var(--ring-food)'}
                size={84}
                stroke={11}
                label={`${fmtNumber(eaten, 0)} of ${fmtNumber(budget, 0)} kcal`}
              >
                <Icon name="utensils" size={22} className="fd-ring-icon" />
              </Ring>
            </span>
          ) : null}
        </div>

        {macros.length > 0 && (
          <div className="hb-bars">
            {macros.map((p) => (
              <div className={`hb-bar${p.over ? ' hb-bar--over' : ''}`} key={p.key}>
                <div className="hb-bar__head">
                  <span className="hb-bar__label">
                    {p.label}
                    {p.kind === 'ceiling' && <span className="fd-ceiling"> · limit</span>}
                  </span>
                  <span className="hb-bar__value">
                    <b className="num">{fmtNumber(p.value, 0)}</b> / {fmtNumber(p.target, 0)} {TARGET_UNITS[p.key]}
                  </span>
                </div>
                <div className="hb-bar__track">
                  <div
                    className="hb-bar__fill"
                    style={{ width: `${Math.round(p.pct * 100)}%`, ['--bar-color' as string]: MACRO_COLOR[p.key] } as CSSProperties}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="fd-actions">
          <button type="button" className="ui-btn ui-btn--primary ui-btn--md" onClick={() => openEntry('describe')}>
            <Icon name="plus" size={18} />
            Add meal
          </button>
          <button type="button" className="ui-btn ui-btn--secondary ui-btn--md" onClick={() => openEntry('scan')}>
            <Icon name="target" size={18} />
            Scan
          </button>
        </div>
      </Card>

      {adding && <MealEntry initialTab={entryTab} onSaved={onSaved} onCancel={() => setAdding(false)} />}

      {loading && recents.length === 0 ? (
        <Group header="Log again">
          {[0, 1, 2].map((i) => (
            <li className="hb-skelrow" key={i} aria-hidden="true" />
          ))}
        </Group>
      ) : (
        <Group header="Log again" footer={recents.length > 0 ? 'One tap logs the same portion now.' : undefined}>
          {shownRecents.map((item) => {
            const done = logged === item.key;
            return (
              <Row
                key={item.key}
                title={item.name}
                sub={
                  <>
                    {item.brand ? `${item.brand} · ` : ''}
                    {item.lastQty !== null ? `${fmtNumber(item.lastQty)} ${item.lastUnit ?? ''} · ` : ''}
                    {item.calories !== null ? `${fmtNumber(item.calories, 0)} kcal` : '—'}
                  </>
                }
                trailing={
                  <button
                    type="button"
                    className={`fd-relog${done ? ' fd-relog--done' : ''}`}
                    aria-label={`Log ${item.name} again`}
                    onClick={() => void repeatFood(item)}
                    disabled={repeating !== null}
                  >
                    <Icon name={done ? 'check' : 'plus'} size={20} />
                  </button>
                }
              />
            );
          })}
          {!allRecents && (
            <MoreRow count={recents.length - RECENT_SHOWN} onClick={() => setAllRecents(true)} />
          )}
        </Group>
      )}

      {loading && meals.length === 0 ? null : meals.length === 0 ? (
        <section className="ui-groupwrap">
          <div className="ui-group__head">
            <h2 className="ui-group__title">Today's meals</h2>
          </div>
          <p className="fd-empty">Nothing logged yet. Tap a food above, or Add meal.</p>
        </section>
      ) : (
        <Group header="Today's meals">
          {meals.map((meal) => {
            const t = itemTotals(meal.items);
            const names = meal.items.map((it) => it.name).join(', ');
            return (
              <Row
                key={meal.id}
                leading={<span className="fd-time num">{timeOfDay(meal.eatenAt)}</span>}
                title={meal.name ?? 'Meal'}
                sub={
                  <>
                    {meal.items.length > 1 || (meal.items[0] && meal.items[0].name !== meal.name) ? (
                      <span className="fd-mealitems">{names}</span>
                    ) : null}
                    <span className="fd-macros num">
                      {fmtNumber(t.protein_g, 0)}P · {fmtNumber(t.carbs_g, 0)}C · {fmtNumber(t.fat_g, 0)}F
                      {t.caffeine_mg > 0 ? ` · ${fmtNumber(t.caffeine_mg, 0)} mg caf` : ''}
                    </span>
                  </>
                }
                detail={<span className="num">{fmtNumber(t.calories, 0)}</span>}
              />
            );
          })}
        </Group>
      )}

      <section className="ui-groupwrap" aria-label="This week">
        <div className="ui-group__head">
          <h2 className="ui-group__title">This week</h2>
          {goal ? <span className="fd-weekgoal num">Goal {fmtNumber(goal, 0)}</span> : null}
        </div>
        <div className="fd-weekcard">
          <div className="hb-week">
            {week.map((d) => {
              const over = goal && d.calories !== null && d.calories > goal;
              return (
                <div
                  className={`hb-week__day${d.date === todayYmd ? ' hb-week__day--today' : ''}`}
                  key={d.date}
                  title={d.calories !== null ? `${fmtNumber(d.calories, 0)} kcal` : 'Nothing logged'}
                >
                  <span className="hb-week__dow">{d.label}</span>
                  <span className="hb-week__col">
                    <span
                      className="hb-week__fill"
                      style={
                        {
                          height: `${Math.round(((d.calories ?? 0) / weekMax) * 100)}%`,
                          '--bar-color': over ? 'var(--warn)' : 'var(--ring-food)',
                        } as CSSProperties
                      }
                    />
                  </span>
                  <span className="hb-week__val">
                    {d.calories !== null ? fmtK(d.calories) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </Page>
  );
}

const MACRO_COLOR: Record<string, string> = {
  protein_g: 'var(--ring-move)',
  fiber_g: 'var(--good)',
  sugar_g: 'var(--ring-water)',
};

/** 1,863 → "1.9k" so seven columns fit a phone. */
function fmtK(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : fmtNumber(n, 0);
}
